import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ProductCard } from "../components/ProductCard";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { LeadCaptureModal } from "../components/LeadCaptureModal";
import { Reviews } from "../components/Reviews";
import { Stars } from "../components/Stars";
import { JsonLd } from "../components/JsonLd";
import { IfEnabled } from "../components/IfEnabled";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useToggle } from "../contexts/ToggleContext";
import { customerApi } from "../lib/customerApi";
import { addToGuestCart, addToGuestWishlist } from "../lib/guestStorage";
import { api, formatINR } from "../lib/api";

export default function ProductDetail() {
    const { slug } = useParams();
    const { user } = useCustomerAuth();
    const leadCaptureOn = useToggle("lead_capture");
    const reviewsOn = useToggle("product_reviews");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeIdx, setActiveIdx] = useState(0);
    const [selectedSize, setSelectedSize] = useState(null);
    const [leadModal, setLeadModal] = useState({ open: false, action: null });

    const fetchProduct = useCallback(() => {
        setLoading(true);
        setActiveIdx(0);
        return api.get(`/products/${slug}`)
            .then(({ data }) => setData(data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => { fetchProduct(); }, [fetchProduct]);

    if (loading) {
        return (
            <div className="min-h-screen bg-ivory">
                <Header />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 text-inkMuted">Loading…</div>
                <Footer />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-ivory">
                <Header />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
                    <h1 className="font-display text-3xl text-inkDark">Piece not found</h1>
                    <p className="mt-3 text-inkMuted">It may have moved or been retired from the collection.</p>
                    <Link to="/" className="btn-pill mt-6 inline-flex">Back home</Link>
                </div>
                <Footer />
            </div>
        );
    }

    const { product, related } = data;
    const images = product.images && product.images.length ? product.images : [];
    const cover = images[activeIdx] || images[0];
    const hasCompare = product.compareAtPrice && product.compareAtPrice > product.price;

    const SITE_URL = (process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
    const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        image: images,
        description: product.description,
        sku: product.id,
        brand: { "@type": "Brand", name: "Sukaanyas" },
        offers: {
            "@type": "Offer",
            url: SITE_URL ? `${SITE_URL}/product/${product.slug}` : undefined,
            priceCurrency: "INR",
            price: product.price,
            availability: product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        },
        ...(product.reviewCount > 0 ? {
            aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: product.avgRating,
                reviewCount: product.reviewCount,
            },
        } : {}),
    };

    const performAddToCart = async () => {
        if ((product.sizes || []).length && !selectedSize) {
            toast.error("Please choose a size first");
            return;
        }
        if (user) {
            try {
                await customerApi.post("/cart/items", {
                    productId: product.id,
                    size: selectedSize || null,
                    qty: 1,
                });
                toast.success(`${product.name} added to your bag`);
            } catch (e2) {
                toast.error("Could not add to bag. Please try again.");
            }
        } else {
            addToGuestCart(product.id, selectedSize || null, 1);
            toast.success(`${product.name} reserved in your bag`);
        }
    };

    const performAddToWishlist = async () => {
        if (user) {
            try {
                await customerApi.post("/wishlist", { productId: product.id });
                toast.success(`${product.name} saved to your wishlist`);
            } catch (e2) {
                toast.error("Could not save. Please try again.");
            }
        } else {
            addToGuestWishlist(product.id);
            toast.success(`${product.name} saved to your wishlist`);
        }
    };

    const handleAddToCart = () => {
        if ((product.sizes || []).length && !selectedSize) {
            toast.error("Please choose a size first");
            return;
        }
        if (!user && leadCaptureOn) {
            setLeadModal({ open: true, action: "cart" });
            return;
        }
        performAddToCart();
    };

    const handleAddToWishlist = () => {
        if (!user && leadCaptureOn) {
            setLeadModal({ open: true, action: "wishlist" });
            return;
        }
        performAddToWishlist();
    };

    const handleLeadComplete = () => {
        // Always proceed with the action whether the user gave email or skipped.
        if (leadModal.action === "cart") performAddToCart();
        else if (leadModal.action === "wishlist") performAddToWishlist();
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="product-detail-page">
            <JsonLd id="ld-product" data={productSchema} />
            <Header />
            <nav className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-6 text-sm text-inkMuted">
                <Link to="/" className="hover:text-skyblueDeep">Home</Link>
                <span className="mx-2">/</span>
                <Link to={`/category/${product.category}`} className="hover:text-skyblueDeep capitalize">{product.category.replace(/-/g, " ")}</Link>
                <span className="mx-2">/</span>
                <span className="text-inkDark">{product.name}</span>
            </nav>

            <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Gallery - 60% */}
                <div className="lg:col-span-7">
                    <div className="relative rounded-2xl overflow-hidden bg-beige" data-testid="product-gallery">
                        <div className="aspect-[4/5]">
                            {cover ? (
                                <img src={cover} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
                            ) : null}
                        </div>
                        {images.length > 1 ? (
                            <>
                                <button
                                    onClick={() => setActiveIdx((i) => (i - 1 + images.length) % images.length)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-ivory/90 hover:bg-ivory rounded-full p-2 shadow-soft"
                                    aria-label="Previous image"
                                    data-testid="gallery-prev-button"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    onClick={() => setActiveIdx((i) => (i + 1) % images.length)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-ivory/90 hover:bg-ivory rounded-full p-2 shadow-soft"
                                    aria-label="Next image"
                                    data-testid="gallery-next-button"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </>
                        ) : null}
                    </div>
                    {images.length > 1 ? (
                        <div className="mt-4 grid grid-cols-4 gap-3">
                            {images.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveIdx(i)}
                                    className={`aspect-[3/4] rounded-xl overflow-hidden border ${i === activeIdx ? "border-skyblueDeep" : "border-inkDark/5"}`}
                                    data-testid={`gallery-thumb-${i}`}
                                >
                                    <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                {/* Details - 40% sticky */}
                <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
                    <span className="label-eyebrow">{product.subType || product.fabric || product.category}</span>
                    <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-inkDark tracking-tight leading-tight" data-testid="product-name">
                        {product.name}
                    </h1>
                    {product.reviewCount > 0 ? (
                        <div className="mt-3 flex items-center gap-2 text-sm" data-testid="product-rating-summary">
                            <Stars rating={product.avgRating} size={14} />
                            <span className="text-inkDark">{Number(product.avgRating).toFixed(1)}</span>
                            <span className="text-inkMuted">·</span>
                            <span className="text-inkMuted">{product.reviewCount} {product.reviewCount === 1 ? "review" : "reviews"}</span>
                        </div>
                    ) : null}
                    <div className="mt-4 flex items-baseline gap-3">
                        <span className="font-medium text-2xl text-inkDark" data-testid="product-price">{formatINR(product.price)}</span>
                        {hasCompare ? (
                            <span className="text-inkMuted line-through">{formatINR(product.compareAtPrice)}</span>
                        ) : null}
                    </div>
                    <p className="mt-6 text-inkMuted leading-relaxed" data-testid="product-description">{product.description}</p>

                    <div className="mt-8 grid grid-cols-2 gap-y-3 text-sm">
                        {product.fabric ? (
                            <>
                                <div className="label-eyebrow">Fabric</div>
                                <div className="text-inkDark">{product.fabric}</div>
                            </>
                        ) : null}
                        {product.color ? (
                            <>
                                <div className="label-eyebrow">Colour</div>
                                <div className="text-inkDark">{product.color}</div>
                            </>
                        ) : null}
                        {product.subType ? (
                            <>
                                <div className="label-eyebrow">Type</div>
                                <div className="text-inkDark">{product.subType}</div>
                            </>
                        ) : null}
                        {product.occasion && product.occasion.length ? (
                            <>
                                <div className="label-eyebrow">Occasion</div>
                                <div className="text-inkDark">{product.occasion.join(", ")}</div>
                            </>
                        ) : null}
                    </div>

                    {(product.sizes || []).length ? (
                        <div className="mt-8">
                            <div className="label-eyebrow mb-3">Size</div>
                            <div className="flex flex-wrap gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSelectedSize(s)}
                                        className={`min-w-[3rem] px-4 py-2 rounded-full border text-sm transition-colors ${
                                            selectedSize === s
                                                ? "bg-skyblue text-inkDark border-skyblue"
                                                : "bg-ivory text-inkDark border-inkDark/10 hover:border-skyblueDeep"
                                        }`}
                                        data-testid={`size-option-${s}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="mt-10 flex flex-wrap gap-3">
                        <IfEnabled flag="cart">
                            <button onClick={handleAddToCart} className="btn-pill" data-testid="add-to-cart-button">
                                <ShoppingBag size={16} /> Add to bag
                            </button>
                        </IfEnabled>
                        <IfEnabled flag="wishlist">
                            <button onClick={handleAddToWishlist} className="btn-ghost" data-testid="add-to-wishlist-button">
                                <Heart size={16} /> Wishlist
                            </button>
                        </IfEnabled>
                    </div>

                    <div className="mt-10 pt-6 border-t border-inkDark/5 text-sm text-inkMuted leading-relaxed">
                        <p>&middot; Free shipping across India on orders above &#8377;3,000.</p>
                        <p>&middot; Easy 7-day return on unworn pieces.</p>
                        <p>&middot; Each piece is hand-finished by master artisans.</p>
                    </div>
                </aside>
            </main>

            {related && related.length ? (
                <section className="bg-beige py-16">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                        <h3 className="font-display text-2xl sm:text-3xl text-inkDark tracking-tight mb-8">You may also love</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 lg:gap-8">
                            {related.slice(0, 4).map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            <IfEnabled flag="product_reviews">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <Reviews slug={slug} productId={product.id} refreshProduct={fetchProduct} />
                </div>
            </IfEnabled>

            <Footer />
            <ChatbotWidget />
            <LeadCaptureModal
                open={leadModal.open}
                action={leadModal.action}
                productId={product?.id}
                onClose={() => setLeadModal({ open: false, action: null })}
                onComplete={handleLeadComplete}
            />
        </div>
    );
}
